/**
 * Permission-aware UI helpers.
 *
 * `/api/admin/me` exposes the caller's FLATTENED effective permission
 * strings (`"resource:action"`, wildcards allowed on the GRANT side —
 * `"*:*"`, `"workflows:*"`). These helpers mirror the backend's
 * `Permission.matches` semantics so the frontend can hide navigation
 * the caller has no grant for.
 *
 * This is a UX courtesy, NOT security: every `/api/admin/*` route is
 * gated server-side by `require_permission` regardless of what the
 * sidebar shows. Tags are organizational labels and play no role here
 * (the tag-scoped DSL was removed — ADR-0006, superseded).
 */
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { meApi } from "@/lib/api-me";

/** Does any grant cover this (resource, action)? Wildcards only on the
 *  GRANT side, mirroring backend `check_permission` — including its
 *  action hierarchy: any grant on a resource IMPLIES `read` on it (you
 *  can't write/execute what you can't see), so a `read` check passes
 *  for anyone holding any grant on that resource. */
export function hasPermission(
  grants: readonly string[],
  resource: string,
  action: string,
): boolean {
  const exact = grants.some((g) => {
    const [gRes, gAct] = g.split(":");
    if (!gRes || !gAct) return false;
    return (gRes === "*" || gRes === resource) && (gAct === "*" || gAct === action);
  });
  if (exact) return true;
  // read is implied by holding ANY action on the resource.
  return action === "read" && canSeeResource(grants, resource);
}

/** Does the caller hold ANY grant on this resource? This is the menu
 *  visibility question — a `workflows:read`-only user still sees the
 *  Workflows menu, while a user with no workflows grant at all doesn't. */
export function canSeeResource(grants: readonly string[], resource: string): boolean {
  return grants.some((g) => {
    const gRes = g.split(":")[0];
    return gRes === "*" || gRes === resource;
  });
}

export interface Permissions {
  /** True while `/me` hasn't answered yet — callers show everything
   *  during the first load instead of flashing an empty nav. */
  loading: boolean;
  can: (resource: string, action: string) => boolean;
  canSee: (resource: string) => boolean;
}

/**
 * The caller's permissions, from the shared `["me"]` query (same cache
 * the preferences sync fills — no extra request). Fail-open while
 * loading or on error: the nav shows everything and the backend gates
 * still apply, so a slow `/me` degrades to today's behaviour.
 */
export function usePermissions(): Permissions {
  const query = useQuery({
    queryKey: ["me"],
    queryFn: () => meApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const grants = query.data?.permissions;
  // Memoised on the grants array so consumers can put `can`/`canSee`
  // in hook dependency lists without re-running every render.
  return React.useMemo<Permissions>(() => {
    if (!grants) {
      return { loading: true, can: () => true, canSee: () => true };
    }
    return {
      loading: false,
      can: (resource, action) => hasPermission(grants, resource, action),
      canSee: (resource) => canSeeResource(grants, resource),
    };
  }, [grants]);
}
